// Generated by tutorials/record.cjs from tutorials/articles/h75-surveys.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h75 --text-only` (wording) or
// `node tutorials/record.cjs h75` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'surveys',
  catalogId: 'h75',
  title: 'Satisfaction surveys',
  summary: 'Ask residents what they think of their meals and dining service: build a survey once, plan a session for a meal, record each resident\'s answers, and watch the scores over time.',
  audience: 'Dietary managers and dietary staff who run resident food and dining surveys',
  area: 'reports',
  learn: [
    'Build a survey template and its questions',
    'Choose a question type and a category',
    'Plan a survey session for a date and meal',
    'Record each resident\'s answers, or an anonymous one',
    'Read the scores: overall, by category and over time',
  ],
  keywords: [
    'survey',
    'surveys',
    'satisfaction',
    'satisfaction survey',
    'resident survey',
    'food survey',
    'dining survey',
    'questionnaire',
    'feedback',
    'template',
    'survey template',
    'question',
    'rating',
    'likert',
    'session',
    'survey session',
    'collect responses',
    'responses',
    'anonymous',
    'analysis',
    'survey results',
    'score',
    'needs improvement',
    'trend',
  ],
  steps: [
    {
      id: 'open',
      part: 'Build a survey template',
      heading: 'Open Surveys',
      text: 'In the menu, select Reports, then Surveys.\n\nSurveys opens on your survey templates: the sets of questions you ask. Each has a name, a description, and a check under Active when it can be used for new sessions.',
      media: {
        image: {
          src: '/help/surveys/open.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/open.mp4',
          width: 1280,
          height: 800,
          durationSec: 5,
        },
        alt: 'The Surveys page: the templates Dining Satisfaction, Snack Check and Holiday Meal Feedback, with New Template, Open, Sessions and Analysis in the toolbar.',
      },
    },
    {
      id: 'new',
      part: 'Build a survey template',
      heading: 'Start a new template',
      text: 'Select New Template. Type a Template Name, and a Description that says what the survey is for and when you use it. Keep Active checked.\n\nSelect Save. The template\'s questions appear under it once it is saved.',
      media: {
        image: {
          src: '/help/surveys/new.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/new.mp4',
          width: 1280,
          height: 800,
          durationSec: 6,
        },
        alt: 'A new template named Room Tray Survey with its description and Active checked, after Save: Questions (0) and Add Question in the toolbar.',
      },
    },
    {
      id: 'question',
      part: 'Build a survey template',
      heading: 'Add a question',
      text: 'Select Add Question and type the Question Text, the way you will ask it.\n\nPick the Question Type: Rating (1-5) or Rating (1-10) for a score, Yes / No, or Free Text for a comment.',
      media: {
        image: {
          src: '/help/surveys/question.webp',
          width: 942,
          height: 692,
        },
        clip: {
          src: '/help/surveys/question.mp4',
          width: 942,
          height: 692,
          durationSec: 6,
        },
        alt: 'The Add Question window with the question typed and the Question Type list open: Rating (1-5), Rating (1-10), Yes / No and Free Text.',
      },
    },
    {
      id: 'category',
      part: 'Build a survey template',
      heading: 'Pick its category',
      text: 'Pick a Category, such as Temperature, Taste or Service. On the Collect Responses page, questions are grouped under their category, and Analysis works out a score for each one.\n\nSort Order sets where the question comes in its group. Check Required for a question every resident should answer. Select Save.',
      media: {
        image: {
          src: '/help/surveys/category.webp',
          width: 942,
          height: 692,
        },
        clip: {
          src: '/help/surveys/category.mp4',
          width: 942,
          height: 692,
          durationSec: 5.1,
        },
        alt: 'The question Was your tray hot when it reached your room?, Rating (1-5), Category Temperature, Sort Order 1 and Required checked.',
      },
    },
    {
      id: 'saved',
      part: 'Build a survey template',
      heading: 'Your question is listed',
      text: 'The question is listed under Questions with its number, type and category. Add the rest the same way.\n\nTo change a question, select it and select Edit Question. Delete Question is under More actions.',
      media: {
        image: {
          src: '/help/surveys/saved.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/saved.mp4',
          width: 1280,
          height: 800,
          durationSec: 3,
        },
        alt: 'The Room Tray Survey template with one question: 1, Was your tray hot when it reached your room?, Rating (1-5), Temperature, Required.',
      },
    },
    {
      id: 'sessions',
      part: 'Plan a survey session',
      heading: 'Open Sessions',
      text: 'A session is one round of asking: a template, on a date, at a meal. Select Surveys in the menu, then Sessions.\n\nThe list shows the last six months of sessions, newest first, with how many responses each has. Change From and To to see other dates.',
      media: {
        image: {
          src: '/help/surveys/sessions.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/sessions.mp4',
          width: 1280,
          height: 800,
          durationSec: 5.3,
        },
        alt: 'The survey sessions list: October lunch survey, October snack check, September dinner survey and older, each with its template, date, meal and number of responses.',
      },
    },
    {
      id: 'new-session',
      part: 'Plan a survey session',
      heading: 'Plan a new session',
      text: 'Select New Session. Type a Session Name, pick the Template, and check the Survey Date (it starts on today) and the Mealtime you will ask about. Notes are for anything worth remembering, such as where you asked.\n\nSelect Save.',
      media: {
        image: {
          src: '/help/surveys/new-session.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/new-session.mp4',
          width: 1280,
          height: 800,
          durationSec: 6,
        },
        alt: 'A new session: Session Name November lunch survey, Template Dining Satisfaction, Survey Date today, Mealtime Lunch, and a note.',
      },
    },
    {
      id: 'session-saved',
      part: 'Plan a survey session',
      heading: 'Save the session',
      text: 'Select Save. The session opens with its Responses list, empty for now, and Collect Responses in the toolbar.',
      media: {
        image: {
          src: '/help/surveys/session-saved.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/session-saved.mp4',
          width: 1280,
          height: 800,
          durationSec: 3.3,
        },
        alt: 'The saved November lunch survey session with Responses (0) and Collect Responses in the toolbar.',
      },
    },
    {
      id: 'collect',
      part: 'Collect responses',
      heading: 'Open Collect Responses',
      text: 'Open the session and select Collect Responses. The top of the page shows the session, its date and meal.\n\nThe template\'s questions follow, grouped by category. A red star marks a required question.',
      media: {
        image: {
          src: '/help/surveys/collect.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/collect.mp4',
          width: 1280,
          height: 800,
          durationSec: 2.9,
        },
        alt: 'Collect Responses for the November lunch survey: the Resident box, Anonymous, and the questions under Food Quality, Taste and Temperature with 1 to 5 choices.',
      },
    },
    {
      id: 'resident',
      part: 'Collect responses',
      heading: 'Pick the resident',
      text: 'Pick the resident in Resident: type part of their name to find them.\n\nIf they would rather not give their name, check Anonymous instead. Their answers still count in the scores.',
      media: {
        image: {
          src: '/help/surveys/resident.webp',
          width: 1280,
          height: 852,
        },
        clip: {
          src: '/help/surveys/resident.mp4',
          width: 1280,
          height: 852,
          durationSec: 4.7,
        },
        alt: 'Resident set to Chen, Mei, with Anonymous unchecked beside it.',
      },
    },
    {
      id: 'answer',
      part: 'Collect responses',
      heading: 'Record the answers',
      text: 'Ask each question and select the resident\'s answer. On a 1 to 5 rating, 1 is Poor and 5 is Excellent. Type comments in the Free Text boxes in their own words.',
      media: {
        image: {
          src: '/help/surveys/answer.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/answer.mp4',
          width: 1280,
          height: 800,
          durationSec: 6,
        },
        alt: 'The last questions answered: 4, 3 and 5 for presentation, variety and service, Yes, and a comment typed in the Free Text box.',
      },
    },
    {
      id: 'next',
      part: 'Collect responses',
      heading: 'Save and go on to the next resident',
      text: 'Select Save & Next. The answers are saved and the page clears for the next resident. When you have asked everyone, select Save on the last one, or Cancel to go back to the session.\n\nThe session\'s Responses list shows who answered, when, and who entered it.',
      media: {
        image: {
          src: '/help/surveys/next.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/next.mp4',
          width: 1280,
          height: 800,
          durationSec: 5,
        },
        alt: 'The session\'s Responses list with Chen, Mei, today\'s date and time, entered by Dana Morgan, and Complete checked.',
      },
    },
    {
      id: 'analysis',
      part: 'Read the results',
      heading: 'Open Analysis',
      text: 'Select Surveys in the menu, then Analysis. It covers the last year of sessions.\n\nOverall Score, at the top, is the average of every rating. Categories Tracked counts the categories that have ratings, and Needs Improvement names the category with the lowest average.',
      media: {
        image: {
          src: '/help/surveys/analysis.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/analysis.mp4',
          width: 1280,
          height: 800,
          durationSec: 6,
        },
        alt: 'Survey Analysis: Overall Score 3.9, Categories Tracked 7, and Needs Improvement Variety, marked.',
      },
    },
    {
      id: 'trend',
      part: 'Read the results',
      heading: 'Read the trend',
      text: 'Satisfaction Trends Over Time has a line for each category and a point for each session date. A line that climbs means residents are rating that part of their meals higher.\n\nPoint at a point to see the scores for that date.',
      media: {
        image: {
          src: '/help/surveys/trend.webp',
          width: 1280,
          height: 800,
        },
        alt: 'The trend chart: Temperature climbing from about 3 to over 4 across six months, the other categories steadier.',
      },
    },
    {
      id: 'summary',
      part: 'Read the results',
      heading: 'Compare the categories',
      text: 'Summary by Category puts each category\'s average side by side, from every session in the dates. The shortest bar is the one to work on first.',
      media: {
        image: {
          src: '/help/surveys/summary.webp',
          width: 1280,
          height: 800,
        },
        alt: 'Summary by Category: Service highest at about 4.5, Variety lowest at about 3.2.',
      },
    },
    {
      id: 'filters',
      part: 'Read the results',
      heading: 'Narrow it down',
      text: 'Use From and To at the top to change the dates. Template shows the scores for one survey only. Mealtime narrows the trend chart to the sessions at one meal.',
      media: {
        image: {
          src: '/help/surveys/filters.webp',
          width: 1280,
          height: 800,
        },
        clip: {
          src: '/help/surveys/filters.mp4',
          width: 1280,
          height: 800,
          durationSec: 4.7,
        },
        alt: 'The Template list set to Dining Satisfaction, with From and To and Mealtime beside it.',
      },
    },
  ],
  tips: [
    'Build one template and use it every month. The same questions each time are what make the trend chart worth reading.',
    'Give every rating question a category. The scores on Analysis are worked out by category.',
    'Select Anonymous for a resident who would rather not give their name. The answers still count in the scores.',
  ],
  notes: [
    'The scores average the numbers residents choose on questions that have a category. Yes / No questions count as 1 for Yes and 0 for No, so leave their Category empty to keep them out of the scores. Free Text answers are kept with the response and never scored.',
    'Only active templates can be picked for a new session. Clear Active on a template you no longer use; its sessions and answers stay.',
    'Deleting a template keeps its sessions and responses. Deleting a session deletes its responses too.',
    'Surveys needs Satisfaction Surveys on your role: any level lets you collect responses and read Analysis, and level 2 or higher lets you build templates, plan sessions and delete. Ask your administrator.',
  ],
  related: ['running-a-report', 'roles-and-access', 'getting-around'],
  recorded: '2026-11-03',
};

export default article;
